import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { isEmpty } from 'lodash';
import { message, Spin } from 'antd';
import dayjs from 'dayjs';

import Input from 'Components/CommonComponents/ReactHooKForm/Input/Input';
import Checkbox from 'Components/CommonComponents/ReactHooKForm/Checkbox/Checkbox';
import DatePicker from 'Components/CommonComponents/ReactHooKForm/DatePicker/DatePicker';
import RangePicker from 'Components/CommonComponents/ReactHooKForm/RangePicker/RangePicker';
import TextEdit from 'Components/CommonComponents/TextEdit';
import Button from 'Components/CommonComponents/Button/Button';
import { DEFAULT_ERROR_MESSAGE } from 'Utilities/constants';
import { useCandidateInfo } from 'ServiceHooks/candidatesHooks';
import {
  useAddCandidateExperience,
  useEditCandidateExperience,
} from 'ServiceHooks/candidatesHooks';
import 'Styles/commonStyles.scss';
import { FORM_TYPES } from './ExperienceTab';

export default function ExperienceForm(props) {
  const { mode, close } = props;
  const params = useParams();

  const isEditForm = mode === FORM_TYPES.EDIT;

  const { refetch: refetchCandidates } = useCandidateInfo({
    paramsData: {
      candidateId: params?.candidateId,
    },
    enabled: false,
  });

  const defaultValues = {
    company: isEditForm ? props?.details?.company : '',
    designation: isEditForm ? props?.details?.designation : '',
    isCurrentCompany: isEditForm
      ? props?.details?.is_current_company
      : false,
    workedFrom: isEditForm ? dayjs(props?.details?.worked_from) : '',
    duration: isEditForm
      ? [
          dayjs(props?.details?.worked_from),
          dayjs(props?.details?.worked_till),
        ]
      : [],
    responsibilities: isEditForm ? props?.details?.responsibilities : '',
  };

  const {
    handleSubmit,
    control,
    setValue,
    register,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues,
    mode: 'all',
  });

  useEffect(() => {
    register('responsibilities', { required: 'This Field is Required' });
  }, [register]);

  const isCurrentCompany = useWatch({ control, name: 'isCurrentCompany' });
  const [messageApi, contextHolder] = message.useMessage();

  const { mutateAsync: addExperience, isLoading: isAdding } =
    useAddCandidateExperience({
      onSuccess: async () => {
        await messageApi.open({
          type: 'success',
          content: 'Adding Complete.',
        });
      },
      onError: async (error) => {
        await messageApi.open({
          type: 'error',
          content: DEFAULT_ERROR_MESSAGE,
        });
        console.error('Experience Error: ', error);
      },
    });

  const { mutateAsync: editExperience, isLoading: isEditing } =
    useEditCandidateExperience({
      onSuccess: async () => {
        await messageApi.open({
          type: 'success',
          content: 'Editing Complete.',
        });
      },
      onError: async (error) => {
        await messageApi.open({
          type: 'error',
          content: DEFAULT_ERROR_MESSAGE,
        });
        console.error('Experience Error: ', error);
      },
    });

  const onTextAreaChange = (text) => {
    if (!isEmpty(errors?.responsibilities) && text.length > 0) {
      clearErrors('responsibilities');
    }
    setValue('responsibilities', text);
  };

  const onSubmit = async (fields) => {
    try {
      const { company, designation, isCurrentCompany, responsibilities } =
        fields;

      const data = {
        candidate: params?.candidateId,
        company,
        designation,
        is_current_company: isCurrentCompany,
        responsibilities,
      };

      if (isCurrentCompany) {
        data['worked_from'] = dayjs(fields.workedFrom).format(
          'YYYY-MM-DD',
        );
      } else {
        data['worked_from'] = dayjs(fields.duration[0]).format(
          'YYYY-MM-DD',
        );
        data['worked_till'] = dayjs(fields.duration[1]).format(
          'YYYY-MM-DD',
        );
      }

      if (isEditForm) {
        await editExperience({
          body: data,
          experienceId: props?.details?.id,
        });
      } else {
        await addExperience(data);
      }
      refetchCandidates();
      close();
    } catch (error) {
      console.error('Submission Error', error);
    }
  };

  const isMutating = isAdding || isEditing;

  return (
    <div className="w-full px-32 pt-8">
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
        <div className="w-full">
          <Input
            label="Company"
            name="company"
            control={control}
            rules={{
              required: 'This Field is Required',
              pattern: {
                value: /[a-zA-Z]/g,
                message: 'Invalid Company Name',
              },
            }}
            placeholder="Name"
          />
        </div>
        <div className="w-full">
          <Input
            label="Designation"
            name="designation"
            placeholder="Title/Role Name"
            control={control}
            rules={{
              required: 'This Field is Required',
              pattern: {
                value: /[a-zA-Z]/g,
                message: 'Please enter letters only',
              },
            }}
          />
        </div>
        <div className="w-full">
          <Checkbox
            label="Current Company?"
            name="isCurrentCompany"
            control={control}
            showUpperLabel={false}
          />
        </div>
        <div className="w-full">
          {isCurrentCompany ? (
            <DatePicker
              label="Starting Date"
              name="workedFrom"
              control={control}
              noTextInput
              rules={{
                required: 'Please Choose a Date',
              }}
              disableFutureDays
            />
          ) : (
            <RangePicker
              label="Duration"
              name="duration"
              control={control}
              rules={{
                required: 'Please Choose a Date',
              }}
              disableFutureDays
            />
          )}
        </div>
        <div>
          <div>Responsibilities</div>
          <TextEdit
            initialValue={
              isEditForm ? props?.details?.responsibilities : ''
            }
            onChange={onTextAreaChange}
          />
          {errors?.responsibilities && (
            <div className="text-errorMain">
              {errors?.responsibilities?.message}
            </div>
          )}
        </div>

        <div className="flex ml-auto mt-4 gap-4">
          {contextHolder}
          <Button
            isCancel
            htmlType="submit"
            disabled={isMutating}
            children="Cancel"
            onClick={close}
          />
          <Button type="primary" htmlType="submit" disabled={isMutating}>
            {isMutating ? (
              <Spin className="loadingSpinner" size="default" />
            ) : (
              'Add'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
